import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import mermaid from 'mermaid';

// Initialize mermaid once at module level to avoid repeated init calls
mermaid.initialize({
  startOnLoad: false,
  theme: 'base',
  themeVariables: {
    primaryColor: '#e0e7ff',
    primaryTextColor: '#312e81',
    primaryBorderColor: '#6366f1',
    lineColor: '#6366f1',
    secondaryColor: '#f0fdf4',
    tertiaryColor: '#fef3c7',
    fontSize: '14px'
  },
  flowchart: { useMaxWidth: true, htmlLabels: true, curve: 'basis' },
  securityLevel: 'loose'
});

// Serialize mermaid.render() calls to prevent concurrent rendering failures
let mermaidRenderQueue = Promise.resolve();
function queuedMermaidRender(id, text) {
  return new Promise((resolve, reject) => {
    mermaidRenderQueue = mermaidRenderQueue.then(async () => {
      try {
        const result = await mermaid.render(id, text);
        resolve(result);
      } catch (err) {
        reject(err);
      }
    });
  });
}

// Fix common LLM mermaid syntax mistakes
function sanitizeMermaid(text) {
  let lines = text.split('\n');
  let subgraphCount = 0;

  lines = lines.map(line => {
    let s = line;

    // Fix subgraph titles: "subgraph Some Title" → "subgraph sg1[Some Title]"
    const subgraphMatch = s.match(/^(\s*)subgraph\s+(.+)$/);
    if (subgraphMatch) {
      const indent = subgraphMatch[1];
      const title = subgraphMatch[2].trim();
      // If already has bracket syntax like subgraph id[title], leave it
      if (!title.includes('[')) {
        subgraphCount++;
        return `${indent}subgraph sg${subgraphCount}[${title}]`;
      }
      return s;
    }

    // Fix unclosed edge labels: count pipes on this line
    const pipeCount = (s.match(/\|/g) || []).length;
    if (pipeCount % 2 !== 0) {
      const lastPipe = s.lastIndexOf('|');
      s = s.substring(0, lastPipe) + '|' + s.substring(lastPipe + 1).trim() + '|';
    }

    // Split chained arrows: "F[label] --> C" on same line as "D -->|No| F[label] --> C"
    // Mermaid v11 doesn't support chained arrows — split into separate edges
    const chainMatch = s.match(/^(.+\]\s*)(-->)\s*([A-Za-z_]\w*)$/);
    if (chainMatch && s.indexOf('-->') !== s.lastIndexOf('-->')) {
      // Has multiple arrows — keep only up to the node definition
      const firstArrow = s.indexOf('-->');
      const afterFirst = s.indexOf(']', firstArrow);
      if (afterFirst > 0) {
        s = s.substring(0, afterFirst + 1);
      }
    }

    // Remove |> artifacts
    s = s.replace(/\|>/g, '|');

    // Clean special chars from edge labels (between |...|)
    s = s.replace(/\|([^|]*)\|/g, (m, label) => '|' + label.replace(/[";{}()[\]#&]/g, '').trim() + '|');

    // Clean special chars from node labels in brackets [...]
    s = s.replace(/\[([^\]]*)\]/g, (m, label) => '[' + label.replace(/[";{}()#&]/g, '').trim() + ']');

    // Remove ? from decision nodes {...}
    s = s.replace(/\{([^}]*)\?([^}]*)\}/g, '{$1$2}');

    // Fix self-referencing edges (E --> E)
    s = s.replace(/^(\s*)([A-Za-z_]\w*)\s*-->\s*\2\s*$/g, '');

    return s;
  });

  let s = lines.join('\n');

  // Fix decision nodes without IDs: --> {text} NodeId
  s = s.replace(/(-->|---)\s*\{([^}]+)\}\s*([A-Za-z_]\w*)/g, '$1 $3{$2}');
  let decCounter = 0;
  s = s.replace(/^(\s*)\{([^}]+)\}\s*(-->|---)/gm, (match, indent, label, arrow) => {
    decCounter++;
    return `${indent}dec${decCounter}{${label}} ${arrow}`;
  });

  return s;
}

function PolicyDiagramRenderer({ diagramData }) {
  const containerRef = useRef(null);
  const [svgContent, setSvgContent] = useState('');
  const [error, setError] = useState(null);

  const diagram = diagramData?.mermaidDiagram || diagramData;
  const description = diagramData?.description || '';
  const title = diagramData?.title || 'Diagram';

  useEffect(() => {
    if (!diagram) return;
    const renderDiagram = async () => {
      let diagramText = typeof diagram === 'string' ? diagram.replace(/\\n/g, '\n') : '';
      diagramText = sanitizeMermaid(diagramText);
      try {
        const id = 'policy-diagram-' + Math.random().toString(36).substr(2, 9);
        const { svg } = await queuedMermaidRender(id, diagramText);
        setSvgContent(svg);
        setError(null);
      } catch (err) {
        console.error('Mermaid render error:', err, '\nDiagram text:', diagramText);
        // Try a simplified version — strip subgraphs which cause issues in mermaid v11
        try {
          let simplified = diagramText
            .replace(/^\s*subgraph\s+.*$/gm, '')
            .replace(/^\s*end\s*$/gm, '');
          const retryId = 'policy-diagram-retry-' + Math.random().toString(36).substr(2, 9);
          const { svg } = await queuedMermaidRender(retryId, simplified);
          setSvgContent(svg);
          setError(null);
          return;
        } catch (retryErr) {
          console.error('Mermaid retry also failed:', retryErr);
        }
        setError('Could not render diagram');
      }
    };
    renderDiagram();
  }, [diagram]);

  if (!diagram) return null;

  return (
    <div className="mb-6">
      <h4 className="text-lg font-semibold text-gray-900 mb-2">{title}</h4>
      {description && <p className="text-sm text-gray-600 mb-3">{description}</p>}
      {error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">{error}</div>
      ) : svgContent ? (
        <div ref={containerRef} className="overflow-x-auto bg-white border border-gray-200 rounded-lg p-4 flex justify-center"
          dangerouslySetInnerHTML={{ __html: svgContent }} />
      ) : (
        <div className="flex items-center justify-center p-8 text-gray-400">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500 mr-3"></div>
          Rendering diagram...
        </div>
      )}
    </div>
  );
}

function ProcessDiagramAccordion({ diagramData }) {
  const [expanded, setExpanded] = useState(false);
  const containerRef = useRef(null);
  const [svgContent, setSvgContent] = useState('');
  const [error, setError] = useState(null);

  const diagram = diagramData?.mermaidDiagram || diagramData;
  const description = diagramData?.description || '';
  const title = diagramData?.title || 'Process Diagram';

  useEffect(() => {
    if (!diagram) return;
    const renderDiagram = async () => {
      let diagramText = typeof diagram === 'string' ? diagram.replace(/\\n/g, '\n') : '';
      diagramText = sanitizeMermaid(diagramText);
      try {
        const id = 'policy-process-' + Math.random().toString(36).substr(2, 9);
        const { svg } = await queuedMermaidRender(id, diagramText);
        setSvgContent(svg);
        setError(null);
      } catch (err) {
        console.error('Mermaid render error:', err, '\nDiagram text:', diagramText);
        // Retry with simplified version — strip subgraphs
        try {
          let simplified = diagramText
            .replace(/^\s*subgraph\s+.*$/gm, '')
            .replace(/^\s*end\s*$/gm, '');
          const retryId = 'policy-process-retry-' + Math.random().toString(36).substr(2, 9);
          const { svg } = await queuedMermaidRender(retryId, simplified);
          setSvgContent(svg);
          setError(null);
          return;
        } catch (retryErr) {
          console.error('Mermaid retry also failed:', retryErr);
        }
        setError('Could not render diagram');
      }
    };
    renderDiagram();
  }, [diagram]);

  if (!diagram) return null;

  return (
    <div className="mb-4 border border-gray-200 rounded-lg overflow-hidden">
      {!expanded && (
        <button onClick={() => setExpanded(true)}
          className="w-full flex items-center px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left">
          <div className="flex-1 min-w-0 mr-4">
            <h4 className="text-base font-semibold text-gray-900">{title}</h4>
            {description && <p className="text-sm text-gray-500 mt-0.5 line-clamp-2">{description}</p>}
            <span className="text-xs text-indigo-600 font-medium mt-1 inline-block">Click to expand</span>
          </div>
          {svgContent && (
            <div className="flex-shrink-0 overflow-hidden rounded border border-gray-200 bg-white relative"
              style={{ width: '160px', height: '100px' }}>
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center"
                style={{ transform: 'scale(0.25)', transformOrigin: 'center center' }}>
                <div dangerouslySetInnerHTML={{ __html: svgContent }} />
              </div>
            </div>
          )}
          {!svgContent && !error && (
            <div className="flex-shrink-0 w-[160px] h-[100px] rounded border border-gray-200 bg-white flex items-center justify-center">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-400"></div>
            </div>
          )}
          <svg className="w-5 h-5 text-gray-400 flex-shrink-0 ml-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}
      {error && !expanded && (
        <div className="px-4 py-2 bg-red-50 text-red-700 text-sm">{error}</div>
      )}
      {expanded && (
        <div>
          <button onClick={() => setExpanded(false)}
            className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left">
            <div className="flex-1 min-w-0">
              <h4 className="text-base font-semibold text-gray-900">{title}</h4>
              {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
            </div>
            <svg className="w-5 h-5 text-gray-400 flex-shrink-0 ml-3 rotate-180" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {error ? (
            <div className="px-4 py-3 bg-red-50 text-red-700 text-sm">{error}</div>
          ) : svgContent ? (
            <div ref={containerRef} className="overflow-x-auto p-4 flex justify-center"
              dangerouslySetInnerHTML={{ __html: svgContent }} />
          ) : (
            <div className="flex items-center justify-center p-8 text-gray-400">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-500 mr-2"></div>
              Rendering...
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function PolicyAnalysis() {
  const { user, isAdmin } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [inputMode, setInputMode] = useState('url');
  const [textContent, setTextContent] = useState('');
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [isPrivate, setIsPrivate] = useState(false);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [results, setResults] = useState([]);
  const [error, setError] = useState('');
  const [recentAnalyses, setRecentAnalyses] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [folders, setFolders] = useState([]);
  const [selectedFolder, setSelectedFolder] = useState(null);
  const [folderModalOpen, setFolderModalOpen] = useState(false);
  const [folderModalMode, setFolderModalMode] = useState('create');
  const [folderModalName, setFolderModalName] = useState('');
  const [folderModalTarget, setFolderModalTarget] = useState(null);
  const [isEditingMetadata, setIsEditingMetadata] = useState(false);
  const [metadataForm, setMetadataForm] = useState({
    policy_type: '',
    issuing_authority: '',
    effective_date: '',
    review_cycle: '',
    applicability_scope: ''
  });

  useEffect(() => {
    if (id) {
      loadAnalysis(id);
    } else {
      setAnalysis(null);
      setResults([]);
      setLoading(false);
    }
    loadRecentAnalyses();
    if (user) fetchPolicyFolders();
  }, [id]);

  const fetchPolicyFolders = useCallback(async () => {
    try {
      const response = await fetch('/api/policy-folders', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setFolders(data.folders || []);
      }
    } catch (err) {
      console.error('Error fetching policy folders:', err);
    }
  }, []);

  useEffect(() => {
    if (analysis && (analysis.policy_type || analysis.issuing_authority || analysis.effective_date || analysis.review_cycle || analysis.applicability_scope)) {
      setMetadataForm({
        policy_type: analysis.policy_type || '',
        issuing_authority: analysis.issuing_authority || '',
        effective_date: analysis.effective_date || '',
        review_cycle: analysis.review_cycle || '',
        applicability_scope: analysis.applicability_scope || ''
      });
    }
  }, [analysis]);

  async function loadRecentAnalyses() {
    try {
      const response = await fetch('/api/policy-analyses?limit=100');
      const data = await response.json();
      if (data.success) setRecentAnalyses(data.analyses);
    } catch (err) {
      console.error('Failed to load recent analyses:', err);
    }
  }

  const filteredAnalyses = recentAnalyses.filter(item => {
    if (selectedFolder === 'unfiled') {
      if (item.folder_id) return false;
    } else if (selectedFolder) {
      const folder = folders.find(f => f.uuid === selectedFolder);
      if (!folder || item.folder_id !== folder.id) return false;
    }
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      item.title?.toLowerCase().includes(query) ||
      item.url?.toLowerCase().includes(query) ||
      item.status?.toLowerCase().includes(query) ||
      item.policy_type?.toLowerCase().includes(query) ||
      item.issuing_authority?.toLowerCase().includes(query)
    );
  });

  async function handleCreatePolicyFolder() {
    if (!folderModalName.trim()) return;
    try {
      const response = await fetch('/api/policy-folders', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ name: folderModalName.trim() })
      });
      if (response.ok) { setFolderModalOpen(false); setFolderModalName(''); fetchPolicyFolders(); }
    } catch (err) { alert('Error creating folder: ' + err.message); }
  }

  async function handleRenamePolicyFolder() {
    if (!folderModalName.trim() || !folderModalTarget) return;
    try {
      const response = await fetch(`/api/policy-folders/${folderModalTarget}`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ name: folderModalName.trim() })
      });
      if (response.ok) { setFolderModalOpen(false); setFolderModalName(''); setFolderModalTarget(null); fetchPolicyFolders(); }
    } catch (err) { alert('Error renaming folder: ' + err.message); }
  }

  async function handleDeletePolicyFolder(uuid) {
    if (!window.confirm('Delete this folder? Policies will be moved to Unfiled.')) return;
    try {
      const response = await fetch(`/api/policy-folders/${uuid}`, { method: 'DELETE', credentials: 'include' });
      if (response.ok) { if (selectedFolder === uuid) setSelectedFolder(null); fetchPolicyFolders(); loadRecentAnalyses(); }
    } catch (err) { alert('Error deleting folder: ' + err.message); }
  }

  async function handleMovePolicyToFolder(policyUuid, folderUuid) {
    try {
      const response = await fetch(`/api/policy-analysis/${policyUuid}/folder`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify({ folderUuid })
      });
      if (response.ok) { loadRecentAnalyses(); fetchPolicyFolders(); }
    } catch (err) { alert('Error moving policy: ' + err.message); }
  }

  async function loadAnalysis(uuid) {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`/api/policy-analysis/${uuid}`);
      const data = await response.json();
      if (data.success) {
        setAnalysis(data.analysis);
        setResults(data.results);
        if (data.analysis.status === 'processing') {
          startPolling(uuid);
        } else {
          setLoading(false);
        }
      } else {
        setError(data.error || 'Failed to load analysis');
        setLoading(false);
      }
    } catch (err) {
      setError('Error loading analysis: ' + err.message);
      setLoading(false);
    }
  }

  const isSubmitDisabled = loading || (inputMode === 'url' && !url.trim()) || (inputMode === 'text' && !textContent.trim()) || (inputMode === 'file' && !file);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    setLoading(true);
    setAnalysis(null);
    setResults([]);
    try {
      let response;

      if (inputMode === 'file') {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('private', isPrivate);
        response = await fetch('/api/policy-analysis', {
          method: 'POST',
          body: formData
        });
      } else {
        const payload = { private: isPrivate };
        if (inputMode === 'text') {
          payload.text = textContent;
        } else {
          payload.url = url;
        }
        response = await fetch('/api/policy-analysis', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
      }

      const data = await response.json();
      if (data.success) {
        navigate(`/policy-analysis/${data.uuid}`);
        startPolling(data.uuid);
        loadRecentAnalyses();
      } else {
        setError(data.error || 'Analysis failed');
        setLoading(false);
      }
    } catch (err) {
      setError('Error submitting analysis: ' + err.message);
      setLoading(false);
    }
  }

  function startPolling(uuid) {
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(`/api/policy-analysis/${uuid}`);
        const data = await response.json();
        if (data.success && data.analysis) {
          setAnalysis(data.analysis);
          if (data.analysis.status === 'completed') {
            setResults(data.results || []);
            setLoading(false);
            clearInterval(pollInterval);
          } else if (data.analysis.status === 'failed') {
            setError('Analysis failed. Please try again.');
            setLoading(false);
            clearInterval(pollInterval);
          }
        }
      } catch (err) { console.error('Polling error:', err); }
    }, 3000);
    setTimeout(() => {
      clearInterval(pollInterval);
      if (loading) { setError('Analysis timed out. Please try again.'); setLoading(false); }
    }, 600000);
  }

  async function handleDelete() {
    if (!window.confirm('Are you sure you want to delete this analysis? This action cannot be undone.')) return;
    try {
      const response = await fetch(`/api/policy-analysis/${analysis.uuid}`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) { setAnalysis(null); setResults([]); navigate('/policy-analysis'); loadRecentAnalyses(); }
      else { alert('Error deleting analysis: ' + (data.error || 'Unknown error')); }
    } catch (err) { alert('Error deleting analysis: ' + err.message); }
  }

  async function handleDeleteFromList(uuid, e) {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this analysis? This action cannot be undone.')) return;
    try {
      const response = await fetch(`/api/policy-analysis/${uuid}`, {
        method: 'DELETE', headers: { 'Content-Type': 'application/json' }
      });
      const data = await response.json();
      if (data.success) {
        if (analysis?.uuid === uuid) { setAnalysis(null); setResults([]); navigate('/policy-analysis'); }
        loadRecentAnalyses();
      } else { alert('Error deleting analysis: ' + (data.error || 'Unknown error')); }
    } catch (err) { alert('Error deleting analysis: ' + err.message); }
  }

  async function handleReanalyze() {
    if (!analysis?.uuid) return;
    setError('');
    setLoading(true);
    setResults([]);
    try {
      const response = await fetch(`/api/policy-analysis/${analysis.uuid}/reanalyze`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include'
      });
      if (!response.ok) {
        const errText = await response.text();
        throw new Error(response.status === 401 ? 'Please log in to re-analyze.' : `Server error: ${errText}`);
      }
      const data = await response.json();
      if (data.success) {
        setAnalysis({ ...analysis, status: 'processing', progress: 0, progress_message: 'Starting re-analysis...' });
        startPolling(data.uuid);
      } else { setError(data.error || 'Re-analysis failed'); setLoading(false); }
    } catch (err) { setError('Error starting re-analysis: ' + err.message); setLoading(false); }
  }

  function handleEditMetadata() {
    setMetadataForm({
      policy_type: analysis.policy_type || '',
      issuing_authority: analysis.issuing_authority || '',
      effective_date: analysis.effective_date || '',
      review_cycle: analysis.review_cycle || '',
      applicability_scope: analysis.applicability_scope || ''
    });
    setIsEditingMetadata(true);
  }

  function handleCancelEdit() { setIsEditingMetadata(false); }

  async function handleSaveMetadata(e) {
    e.preventDefault();
    try {
      const response = await fetch(`/api/policy-analysis/${analysis.uuid}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        credentials: 'include', body: JSON.stringify(metadataForm)
      });
      const data = await response.json();
      if (data.success) {
        setAnalysis(data.analysis);
        if (isEditingMetadata && data.analysis.status === 'completed') setIsEditingMetadata(false);
        loadRecentAnalyses();
        if (data.analysis.status === 'processing') alert('Metadata updated successfully!');
      } else { alert('Error updating metadata: ' + (data.error || 'Unknown error')); }
    } catch (err) { alert('Error updating metadata: ' + err.message); }
  }

  function renderVisualization(result) {
    const { graphical_data, visualization_type, categories } = result;
    if (!graphical_data || graphical_data.length === 0) {
      return <p className="text-gray-500 italic">No visualization data available</p>;
    }
    const cats = typeof categories === 'string' ? JSON.parse(categories) : categories;

    if (visualization_type === 'readiness-chart') return renderReadinessChart(graphical_data, cats);
    if (visualization_type === 'burden-chart') return renderBurdenChart(graphical_data, cats);
    if (visualization_type === 'risk-matrix') return renderRiskMatrix(graphical_data, cats);
    if (visualization_type === 'stakeholder-risks') return renderStakeholderRisks(graphical_data, cats);
    if (visualization_type === 'consequences-chart') return renderConsequencesChart(graphical_data, cats);

    return <p className="text-gray-500 italic">Unknown visualization type</p>;
  }

  function renderReadinessChart(data, categories) {
    const readyItems = data.filter(d => ['clear-timeline', 'assigned-owner', 'measurable-metric', 'defined-process'].includes(d.category));
    const gapItems = data.filter(d => ['missing-timeline', 'unclear-ownership', 'no-metrics', 'vague-process'].includes(d.category));

    const readyCount = readyItems.length;
    const gapCount = gapItems.length;
    const total = readyCount + gapCount;
    const readinessScore = total > 0 ? Math.round((readyCount / total) * 100) : 0;

    const scoreColor = readinessScore >= 70 ? 'text-green-600' : readinessScore >= 40 ? 'text-yellow-600' : 'text-red-600';
    const barColor = readinessScore >= 70 ? 'bg-green-500' : readinessScore >= 40 ? 'bg-yellow-500' : 'bg-red-500';

    return (
      <div className="space-y-4">
        <div className="text-center">
          <span className={`text-4xl font-bold ${scoreColor}`}>{readinessScore}%</span>
          <p className="text-sm text-gray-600 mt-1">Implementation Readiness Score</p>
          <div className="w-full max-w-md mx-auto bg-gray-200 rounded-full h-3 mt-2 overflow-hidden">
            <div className={`h-3 rounded-full transition-all ${barColor}`} style={{ width: `${readinessScore}%` }}></div>
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <h5 className="font-semibold text-green-700 mb-2">Ready ({readyItems.length})</h5>
            <div className="space-y-2">
              {readyItems.map((item, idx) => {
                const cat = categories.find(c => c.value === item.category);
                return (
                  <div key={idx} className="bg-green-50 border border-green-200 rounded p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-green-600 font-bold text-sm">&#10003;</span>
                      <span className="text-xs font-semibold text-green-700 uppercase">{cat?.label || item.category}</span>
                    </div>
                    <p className="text-sm font-medium text-gray-900">{item.provision}</p>
                    <p className="text-xs text-gray-600 mt-1">{item.explanation}</p>
                  </div>
                );
              })}
            </div>
          </div>
          <div>
            <h5 className="font-semibold text-red-700 mb-2">Gaps ({gapItems.length})</h5>
            <div className="space-y-2">
              {gapItems.map((item, idx) => {
                const cat = categories.find(c => c.value === item.category);
                return (
                  <div key={idx} className="bg-red-50 border border-red-200 rounded p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-red-600 font-bold text-sm">&#10007;</span>
                      <span className="text-xs font-semibold text-red-700 uppercase">{cat?.label || item.category}</span>
                    </div>
                    <p className="text-sm font-medium text-gray-900">{item.provision}</p>
                    <p className="text-xs text-gray-600 mt-1">{item.explanation}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function renderBurdenChart(data, categories) {
    const burdenLevels = ['extreme-burden', 'high-burden', 'moderate-burden', 'low-burden', 'minimal-burden'];
    const burdenColors = {
      'extreme-burden': 'bg-red-100 border-red-400 text-red-900',
      'high-burden': 'bg-orange-100 border-orange-400 text-orange-900',
      'moderate-burden': 'bg-yellow-100 border-yellow-400 text-yellow-900',
      'low-burden': 'bg-blue-100 border-blue-400 text-blue-900',
      'minimal-burden': 'bg-green-100 border-green-400 text-green-900'
    };

    return (
      <div className="space-y-3">
        {burdenLevels.map(level => {
          const items = data.filter(d => d.category === level);
          if (items.length === 0) return null;
          const cat = categories.find(c => c.value === level);
          const colorClass = burdenColors[level] || 'bg-gray-100 border-gray-400 text-gray-900';

          return (
            <div key={level} className={`border-l-4 pl-4 py-3 rounded-r ${colorClass}`}>
              <h5 className="font-semibold text-base mb-2">{cat?.label || level}</h5>
              <ul className="space-y-2">
                {items.map((item, idx) => (
                  <li key={idx} className="text-sm">
                    <p className="font-medium">{item.provision}</p>
                    <p className="text-xs mt-1 opacity-80">{item.explanation}</p>
                    {item.impact_level && (
                      <span className="inline-block mt-1 px-2 py-0.5 text-xs bg-white bg-opacity-60 rounded">
                        Burden Level: {item.impact_level}/10
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    );
  }

  function renderConsequencesChart(data, categories) {
    const severityOrder = { 10: 0, 9: 1, 8: 2, 7: 3, 6: 4, 5: 5, 4: 6, 3: 7, 2: 8, 1: 9 };
    const sorted = [...data].sort((a, b) => (severityOrder[a.impact_level] ?? 5) - (severityOrder[b.impact_level] ?? 5));

    const categoryIcons = {
      'perverse-incentive': { icon: '\u21BA', color: 'bg-red-100 border-red-500 text-red-900' },
      'loophole': { icon: '\u26A0', color: 'bg-orange-100 border-orange-500 text-orange-900' },
      'chilling-effect': { icon: '\u2744', color: 'bg-blue-100 border-blue-500 text-blue-900' },
      'disproportionate-impact': { icon: '\u2696', color: 'bg-purple-100 border-purple-500 text-purple-900' },
      'resource-strain': { icon: '\u26A1', color: 'bg-yellow-100 border-yellow-500 text-yellow-900' },
      'conflict-with-existing': { icon: '\u2694', color: 'bg-pink-100 border-pink-500 text-pink-900' },
      'gaming-risk': { icon: '\uD83C\uDFAF', color: 'bg-red-50 border-red-400 text-red-900' },
      'cascade-effect': { icon: '\uD83C\uDF0A', color: 'bg-indigo-100 border-indigo-500 text-indigo-900' }
    };

    // Summary counts by category
    const categoryCounts = {};
    data.forEach(d => { categoryCounts[d.category] = (categoryCounts[d.category] || 0) + 1; });
    const activeCategories = categories.filter(c => categoryCounts[c.value]);

    const highRisk = sorted.filter(d => d.impact_level >= 7);
    const medRisk = sorted.filter(d => d.impact_level >= 4 && d.impact_level < 7);
    const lowRisk = sorted.filter(d => d.impact_level < 4);

    const renderItem = (item, idx) => {
      const catInfo = categoryIcons[item.category] || { icon: '!', color: 'bg-gray-100 border-gray-500 text-gray-900' };
      const cat = categories.find(c => c.value === item.category);
      return (
        <div key={idx} className={`border-l-4 pl-4 py-3 rounded-r ${catInfo.color}`}>
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <span className="text-lg">{catInfo.icon}</span>
              <span className="text-xs font-bold uppercase tracking-wide">{cat?.label || item.category}</span>
            </div>
            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white bg-opacity-60">
              Severity: {item.impact_level}/10
            </span>
          </div>
          <p className="font-medium text-sm mt-1">{item.provision}</p>
          <p className="text-xs mt-1 opacity-80">{item.explanation}</p>
          {item.source_text && (
            <p className="text-xs mt-2 italic opacity-60 border-t border-current border-opacity-20 pt-1">"{item.source_text}"</p>
          )}
        </div>
      );
    };

    return (
      <div className="space-y-6">
        {/* Category summary pills */}
        <div className="flex flex-wrap gap-2">
          {activeCategories.map(cat => {
            const info = categoryIcons[cat.value] || { icon: '!', color: '' };
            return (
              <span key={cat.value} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-white border border-gray-200 shadow-sm">
                <span>{info.icon}</span>
                {cat.label}: {categoryCounts[cat.value]}
              </span>
            );
          })}
        </div>

        {/* Risk tiers */}
        {highRisk.length > 0 && (
          <div>
            <h5 className="font-bold text-red-700 mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-red-500"></span>
              High Severity ({highRisk.length})
            </h5>
            <div className="space-y-3">{highRisk.map(renderItem)}</div>
          </div>
        )}
        {medRisk.length > 0 && (
          <div>
            <h5 className="font-bold text-yellow-700 mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-yellow-500"></span>
              Medium Severity ({medRisk.length})
            </h5>
            <div className="space-y-3">{medRisk.map(renderItem)}</div>
          </div>
        )}
        {lowRisk.length > 0 && (
          <div>
            <h5 className="font-bold text-green-700 mb-3 text-sm uppercase tracking-wide flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-green-500"></span>
              Low Severity ({lowRisk.length})
            </h5>
            <div className="space-y-3">{lowRisk.map(renderItem)}</div>
          </div>
        )}
      </div>
    );
  }

  function renderRiskMatrix(data, categories) {
    return (
      <div className="space-y-3">
        {categories.map(cat => {
          const risks = data.filter(d => d.category === cat.value);
          if (risks.length === 0) return null;

          const colorClasses = {
            'compliance-risk': 'bg-red-100 border-red-500 text-red-900',
            'enforcement-gap': 'bg-orange-100 border-orange-500 text-orange-900',
            'unintended-consequence': 'bg-yellow-100 border-yellow-500 text-yellow-900',
            'penalty-defined': 'bg-green-100 border-green-500 text-green-900',
            'penalty-missing': 'bg-red-100 border-red-400 text-red-900',
            'disproportionate-penalty': 'bg-purple-100 border-purple-500 text-purple-900'
          };
          const colorClass = colorClasses[cat.value] || 'bg-gray-100 border-gray-500 text-gray-900';

          return (
            <div key={cat.value} className={`border-l-4 pl-4 py-2 ${colorClass}`}>
              <h5 className="font-semibold">{cat.label}</h5>
              <ul className="mt-2 space-y-2">
                {risks.map((risk, idx) => (
                  <li key={idx} className="text-sm">
                    <p className="font-medium">{risk.provision}</p>
                    <p className="text-xs mt-1 opacity-80">{risk.explanation}</p>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    );
  }

  function SourceTextModal({ item, type, onClose }) {
    const isRisk = type === 'risk';
    const title = isRisk ? item.risk : item.benefit;
    const severity = isRisk ? item.severity : item.significance;
    const sourceText = item.source_text;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
        <div className="fixed inset-0 bg-black bg-opacity-50"></div>
        <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
          <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 rounded-t-xl flex items-start justify-between">
            <div>
              <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold uppercase mb-2 ${isRisk ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                {severity} {type}
              </span>
              <h3 className="text-lg font-bold text-gray-900">{title}</h3>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 flex-shrink-0">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="px-6 py-4">
            {item.explanation && (
              <div className="mb-4">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-1">Explanation</h4>
                <p className="text-gray-700">{item.explanation}</p>
              </div>
            )}
            <div>
              <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Source Text from the Policy</h4>
              {sourceText ? (
                <blockquote className="border-l-4 border-gray-300 pl-4 py-3 bg-gray-50 rounded-r-lg text-gray-800 italic">
                  "{sourceText}"
                </blockquote>
              ) : (
                <p className="text-gray-500 italic">No specific source text available. Re-analyze to generate source references.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  function StakeholderCard({ stakeholder, onItemClick }) {
    const [expanded, setExpanded] = React.useState(false);
    const impactColors = {
      'positive': 'bg-green-100 text-green-800 border-green-300',
      'negative': 'bg-red-100 text-red-800 border-red-300',
      'mixed': 'bg-yellow-100 text-yellow-800 border-yellow-300'
    };
    const severityColors = {
      'critical': 'bg-red-100 border-red-400 text-red-900',
      'high': 'bg-orange-100 border-orange-400 text-orange-900',
      'moderate': 'bg-yellow-100 border-yellow-400 text-yellow-900',
      'low': 'bg-blue-100 border-blue-400 text-blue-900'
    };
    const significanceColors = {
      'major': 'bg-green-100 border-green-400 text-green-900',
      'moderate': 'bg-emerald-50 border-emerald-300 text-emerald-900',
      'minor': 'bg-teal-50 border-teal-300 text-teal-900'
    };

    const vulnLevel = stakeholder.vulnerability_level || 5;
    const vulnColor = vulnLevel >= 8 ? 'bg-red-500' : vulnLevel >= 5 ? 'bg-orange-500' : 'bg-green-500';
    const risks = stakeholder.risks || [];
    const benefits = stakeholder.benefits || [];

    return (
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="p-4 cursor-pointer hover:bg-gray-50 transition-colors" onClick={() => setExpanded(!expanded)}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <h5 className="font-bold text-gray-900 text-lg">{stakeholder.stakeholder}</h5>
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${impactColors[stakeholder.overall_impact] || impactColors['mixed']}`}>
                {stakeholder.overall_impact === 'positive' ? 'Benefits' : stakeholder.overall_impact === 'negative' ? 'At Risk' : 'Mixed Impact'}
              </span>
            </div>
            <svg className={`w-5 h-5 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs font-medium text-gray-600 w-28 flex-shrink-0">Vulnerability: {vulnLevel}/10</span>
            <div className="flex-1 bg-gray-200 rounded-full h-2.5 overflow-hidden">
              <div className={`h-2.5 rounded-full transition-all ${vulnColor}`} style={{ width: `${vulnLevel * 10}%` }}></div>
            </div>
          </div>
          <div className="flex gap-4 mt-2 text-xs text-gray-600">
            {risks.length > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-red-400"></span>{risks.length} risk{risks.length !== 1 ? 's' : ''}</span>}
            {benefits.length > 0 && <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-green-400"></span>{benefits.length} benefit{benefits.length !== 1 ? 's' : ''}</span>}
          </div>
        </div>
        {expanded && (
          <div className="px-4 pb-4 border-t border-gray-100">
            {risks.length > 0 && (
              <div className="mt-4">
                <h6 className="font-semibold text-red-700 text-sm mb-2 uppercase tracking-wide">Risks</h6>
                <div className="space-y-2">
                  {risks.map((risk, idx) => (
                    <div key={idx} className={`border-l-4 pl-3 py-2 rounded-r cursor-pointer hover:opacity-80 transition-opacity ${severityColors[risk.severity] || severityColors['moderate']}`}
                      onClick={(e) => { e.stopPropagation(); onItemClick(risk, 'risk'); }}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold uppercase">{risk.severity}</span>
                        <span className="text-xs text-gray-500">Click for source</span>
                      </div>
                      <p className="font-medium text-sm">{risk.risk}</p>
                      {risk.explanation && <p className="text-xs mt-1 opacity-80">{risk.explanation}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {benefits.length > 0 && (
              <div className="mt-4">
                <h6 className="font-semibold text-green-700 text-sm mb-2 uppercase tracking-wide">Benefits</h6>
                <div className="space-y-2">
                  {benefits.map((benefit, idx) => (
                    <div key={idx} className={`border-l-4 pl-3 py-2 rounded-r cursor-pointer hover:opacity-80 transition-opacity ${significanceColors[benefit.significance] || significanceColors['moderate']}`}
                      onClick={(e) => { e.stopPropagation(); onItemClick(benefit, 'benefit'); }}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-bold uppercase">{benefit.significance}</span>
                        <span className="text-xs text-gray-500">Click for source</span>
                      </div>
                      <p className="font-medium text-sm">{benefit.benefit}</p>
                      {benefit.explanation && <p className="text-xs mt-1 opacity-80">{benefit.explanation}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {risks.length === 0 && benefits.length === 0 && (
              <p className="text-sm text-gray-500 italic mt-4">No specific risks or benefits identified for this group.</p>
            )}
          </div>
        )}
      </div>
    );
  }

  function StakeholderRisksView({ data }) {
    const [modalItem, setModalItem] = React.useState(null);
    const [modalType, setModalType] = React.useState(null);

    if (!data || data.length === 0) return <p className="text-gray-500 italic">No stakeholder data available</p>;

    const handleItemClick = (item, type) => { setModalItem(item); setModalType(type); };
    const sorted = [...data].sort((a, b) => (b.vulnerability_level || 0) - (a.vulnerability_level || 0));
    const atRisk = sorted.filter(s => s.overall_impact === 'negative');
    const mixed = sorted.filter(s => s.overall_impact === 'mixed');
    const benefiting = sorted.filter(s => s.overall_impact === 'positive');

    return (
      <div className="space-y-6">
        {modalItem && <SourceTextModal item={modalItem} type={modalType} onClose={() => { setModalItem(null); setModalType(null); }} />}
        <div className="flex gap-4 text-sm font-medium">
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-red-400"></span>{atRisk.length} at risk</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-yellow-400"></span>{mixed.length} mixed</span>
          <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-green-400"></span>{benefiting.length} benefit</span>
        </div>
        {atRisk.length > 0 && (
          <div>
            <h5 className="font-bold text-red-700 mb-3 text-sm uppercase tracking-wide">Most Vulnerable Groups</h5>
            <div className="space-y-3">{atRisk.map((s, idx) => <StakeholderCard key={idx} stakeholder={s} onItemClick={handleItemClick} />)}</div>
          </div>
        )}
        {mixed.length > 0 && (
          <div>
            <h5 className="font-bold text-yellow-700 mb-3 text-sm uppercase tracking-wide">Mixed Impact Groups</h5>
            <div className="space-y-3">{mixed.map((s, idx) => <StakeholderCard key={idx} stakeholder={s} onItemClick={handleItemClick} />)}</div>
          </div>
        )}
        {benefiting.length > 0 && (
          <div>
            <h5 className="font-bold text-green-700 mb-3 text-sm uppercase tracking-wide">Benefiting Groups</h5>
            <div className="space-y-3">{benefiting.map((s, idx) => <StakeholderCard key={idx} stakeholder={s} onItemClick={handleItemClick} />)}</div>
          </div>
        )}
      </div>
    );
  }

  function renderStakeholderRisks(data) {
    return <StakeholderRisksView data={data} />;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-gray-900 mb-2">Policy Analysis</h1>
        <p className="text-lg text-gray-600">
          Analyze policy documents across multiple dimensions: implementation readiness, compliance burden, responsibilities, risks, and impact
        </p>
      </div>

      {/* Input Form */}
      {!id && (
        <div className="bg-white rounded-lg shadow-md p-6 mb-8">
          <form onSubmit={handleSubmit}>
            {/* Input mode tabs */}
            <div className="flex gap-1 mb-4 bg-gray-100 rounded-lg p-1 w-fit">
              {[
                { key: 'url', label: 'Paste Link' },
                { key: 'text', label: 'Paste Text' },
                { key: 'file', label: 'Upload File' }
              ].map(tab => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setInputMode(tab.key)}
                  className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    inputMode === tab.key
                      ? 'bg-white text-primary-700 shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  }`}
                  disabled={loading}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* URL input */}
            {inputMode === 'url' && (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Policy Document URL (PDF, HTML, or DOCX)
                </label>
                <div className="flex gap-2 mb-3">
                  <input
                    type="url"
                    value={url}
                    onChange={(e) => setUrl(e.target.value)}
                    placeholder="https://example.com/policy-document.pdf"
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    required
                    disabled={loading}
                  />
                  <button
                    type="submit"
                    disabled={isSubmitDisabled}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {loading ? 'Analyzing...' : 'Analyze'}
                  </button>
                </div>
              </>
            )}

            {/* Text input */}
            {inputMode === 'text' && (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Paste Document Text
                </label>
                <textarea
                  value={textContent}
                  onChange={(e) => setTextContent(e.target.value)}
                  placeholder="Paste the full text of the policy document here..."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 mb-3"
                  rows={8}
                  disabled={loading}
                />
                <div className="mb-3">
                  <button
                    type="submit"
                    disabled={isSubmitDisabled}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium"
                  >
                    {loading ? 'Analyzing...' : 'Analyze'}
                  </button>
                </div>
              </>
            )}

            {/* File input */}
            {inputMode === 'file' && (
              <>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Document (PDF, DOCX, or TXT)
                </label>
                <div className="flex gap-2 mb-3 items-center">
                  <label
                    className={`flex-1 flex items-center justify-center px-4 py-6 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                      dragOver ? 'border-primary-500 bg-primary-50' :
                      file ? 'border-primary-400 bg-primary-50' : 'border-gray-300 hover:border-primary-400 hover:bg-gray-50'
                    } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                    onDragOver={(e) => { e.preventDefault(); if (!loading) setDragOver(true); }}
                    onDragEnter={(e) => { e.preventDefault(); if (!loading) setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setDragOver(false);
                      if (!loading && e.dataTransfer.files.length > 0) {
                        setFile(e.dataTransfer.files[0]);
                      }
                    }}
                  >
                    <div className="text-center">
                      {file ? (
                        <span className="text-sm text-primary-700 font-medium">{file.name}</span>
                      ) : (
                        <>
                          <svg className="mx-auto h-8 w-8 text-gray-400 mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                          </svg>
                          <span className="text-sm text-gray-500">Drop a file here or click to browse</span>
                        </>
                      )}
                    </div>
                    <input
                      type="file"
                      accept=".pdf,.docx,.doc,.txt,.html,.htm"
                      onChange={(e) => setFile(e.target.files[0] || null)}
                      className="hidden"
                      disabled={loading}
                    />
                  </label>
                  <button
                    type="submit"
                    disabled={isSubmitDisabled}
                    className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium self-start mt-4"
                  >
                    {loading ? 'Analyzing...' : 'Analyze'}
                  </button>
                </div>
              </>
            )}

            <div className="flex items-center">
              <input
                type="checkbox"
                id="policy-private-toggle"
                checked={isPrivate}
                onChange={(e) => setIsPrivate(e.target.checked)}
                disabled={loading || !user}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <label htmlFor="policy-private-toggle" className={`ml-2 text-sm ${!user ? 'text-gray-400' : 'text-gray-700'}`}>
                Make this analysis private (only visible to you){!user && ' - requires login'}
              </label>
            </div>
            {error && <p className="mt-2 text-sm text-red-600">{error}</p>}
          </form>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="space-y-6">
          {id && (
            <div className="flex items-center justify-between">
              <button onClick={() => { navigate('/policy-analysis'); setAnalysis(null); setResults([]); setLoading(false); }}
                className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to All Policies
              </button>
              <button onClick={() => { navigate('/policy-analysis'); setAnalysis(null); setResults([]); setLoading(false); }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium">
                New Analysis
              </button>
            </div>
          )}
          <div className="bg-white rounded-lg shadow-md p-12">
            <div className="text-center mb-6">
              <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-gray-600 font-medium">
                {analysis ? `Processing: ${analysis.title}` : id ? 'Loading analysis...' : 'Starting analysis...'}
              </p>
            </div>
            <div className="max-w-2xl mx-auto">
              <div className="mb-2 flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">{analysis?.progress_message || (id ? 'Loading...' : 'Initializing...')}</span>
                <span className="text-sm font-semibold text-primary-600">{analysis?.progress || 0}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
                <div className="bg-primary-600 h-3 rounded-full transition-all duration-500 ease-out" style={{ width: `${analysis?.progress || 0}%` }}></div>
              </div>
              {analysis?.steps && Array.isArray(analysis.steps) && analysis.steps.length > 0 && (
                <div style={{ marginTop: '20px' }}>
                  {analysis.steps.map((step, idx) => {
                    const isCompleted = step.status === 'completed';
                    const isInProgress = step.status === 'in_progress';
                    const isFailed = step.status === 'failed';
                    const isSkipped = step.status === 'skipped';
                    let icon = '\u25CB', color = '#9e9e9e', fontWeight = 'normal', bg = 'transparent';
                    if (isCompleted) { icon = '\u2713'; color = '#2e7d32'; }
                    else if (isInProgress) { icon = '\u25CF'; color = '#1565c0'; fontWeight = 'bold'; bg = '#e3f2fd'; }
                    else if (isFailed) { icon = '\u2717'; color = '#c62828'; }
                    else if (isSkipped) { icon = '\u2014'; color = '#9e9e9e'; }
                    return (
                      <div key={step.name || idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '6px 10px', borderRadius: '6px', backgroundColor: bg, marginBottom: '2px', transition: 'background-color 0.3s' }}>
                        <span style={{ color, fontSize: '16px', width: '20px', textAlign: 'center', animation: isInProgress ? 'pulse 1.5s ease-in-out infinite' : 'none' }}>{icon}</span>
                        <span style={{ color: isInProgress ? '#1565c0' : isCompleted ? '#2e7d32' : isFailed ? '#c62828' : '#666', fontWeight, fontSize: '14px' }}>
                          {step.label}
                          {isSkipped && <span style={{ fontStyle: 'italic', marginLeft: '6px', color: '#999' }}>(skipped)</span>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
              <p className="text-xs text-gray-500 mt-3 text-center">This may take 2-5 minutes depending on document size</p>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <p className="text-red-700">{error}</p>
          <button onClick={() => setError('')} className="text-red-500 text-sm underline mt-1">Dismiss</button>
        </div>
      )}

      {/* Analysis Results */}
      {analysis && analysis.status === 'completed' && !loading && (
        <div className="space-y-6">
          {id && (
            <div className="flex items-center justify-between">
              <button onClick={() => { navigate('/policy-analysis'); setAnalysis(null); setResults([]); setLoading(false); }}
                className="flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back to All Policies
              </button>
              <button onClick={() => { navigate('/policy-analysis'); setAnalysis(null); setResults([]); }}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium">
                New Analysis
              </button>
            </div>
          )}

          {/* Analysis Header */}
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-start justify-between mb-4">
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900">{analysis.title}</h2>
                <div className="flex items-center flex-wrap gap-2 mt-2">
                  {analysis.policy_type && (
                    <span className={`px-2 py-1 rounded text-xs font-semibold uppercase ${
                      analysis.policy_type.includes('Government') ? 'bg-blue-100 text-blue-800' :
                      analysis.policy_type.includes('HR') ? 'bg-purple-100 text-purple-800' :
                      analysis.policy_type.includes('Compliance') ? 'bg-red-100 text-red-800' :
                      analysis.policy_type.includes('Security') ? 'bg-orange-100 text-orange-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {analysis.policy_type}
                    </span>
                  )}
                  {analysis.issuing_authority && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-indigo-100 text-indigo-700">
                      {analysis.issuing_authority}
                    </span>
                  )}
                  {analysis.review_cycle && analysis.review_cycle !== 'Not Specified' && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-teal-100 text-teal-700">
                      Review: {analysis.review_cycle}
                    </span>
                  )}
                  {analysis.applicability_scope && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-amber-100 text-amber-700">
                      {analysis.applicability_scope}
                    </span>
                  )}
                  {analysis.effective_date && (
                    <span className="px-2 py-1 rounded text-xs font-medium bg-green-100 text-green-700">
                      Effective: {analysis.effective_date}
                    </span>
                  )}
                  <span className="text-sm text-gray-500">
                    Analyzed on {new Date(analysis.created_at).toLocaleDateString()}
                  </span>
                </div>
                <a href={analysis.url} target="_blank" rel="noopener noreferrer"
                  className="text-sm text-primary-600 hover:text-primary-700 mt-2 inline-block">
                  View Source Document →
                </a>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  analysis.status === 'completed' ? 'bg-green-100 text-green-800' :
                  analysis.status === 'processing' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {analysis.status}
                </span>
                {/* Share / Visibility controls */}
                {user && (
                  <button
                    onClick={async () => {
                      const newPublic = !!analysis.private;
                      try {
                        const res = await fetch(`/api/policy-analysis/${analysis.uuid}/visibility`, {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json' },
                          credentials: 'include',
                          body: JSON.stringify({ isPublic: newPublic })
                        });
                        if (res.ok) {
                          setAnalysis(prev => ({ ...prev, private: !newPublic }));
                        }
                      } catch {}
                    }}
                    className={`px-3 py-1.5 text-sm rounded-lg font-medium ${
                      analysis.private
                        ? 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                    title={analysis.private ? 'Make public to share' : 'Currently public — click to make private'}
                  >
                    {analysis.private ? 'Private' : 'Public'}
                  </button>
                )}
                {!analysis.private && (
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(window.location.origin + '/policy-analysis/' + analysis.uuid);
                      setCopied(true);
                      setTimeout(() => setCopied(false), 2000);
                    }}
                    className="px-3 py-1.5 text-sm bg-primary-100 text-primary-700 rounded-lg hover:bg-primary-200 font-medium"
                    title="Copy shareable link"
                  >
                    {copied ? 'Copied!' : 'Copy Link'}
                  </button>
                )}
                {user && (
                  <button onClick={handleReanalyze}
                    className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    title="Re-analyze this policy">Re-analyze</button>
                )}
                {isAdmin && (
                  <>
                    <button onClick={handleEditMetadata}
                      className="px-3 py-1.5 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                      title="Edit metadata">Edit Tags</button>
                    <button onClick={handleDelete}
                      className="px-3 py-1.5 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700"
                      title="Delete this analysis">Delete</button>
                  </>
                )}
              </div>
            </div>

            {/* Metadata Edit Form */}
            {user && (isEditingMetadata || (analysis && analysis.status === 'processing' && analysis.progress >= 15)) && (
              <form onSubmit={handleSaveMetadata} className="mt-4 pt-4 border-t border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  {analysis && analysis.status === 'processing' ? 'Review & Correct AI Metadata' : 'Edit Metadata Tags'}
                </h3>
                {analysis && analysis.status === 'processing' && (
                  <p className="text-sm text-gray-600 mb-4">
                    The AI has inferred these tags from your document. You can correct them while the analysis runs in the background.
                  </p>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Policy Type</label>
                    <select value={metadataForm.policy_type}
                      onChange={(e) => setMetadataForm({...metadataForm, policy_type: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                      <option value="">Select type...</option>
                      <option value="Government Regulation">Government Regulation</option>
                      <option value="Corporate Policy">Corporate Policy</option>
                      <option value="HR Policy">HR Policy</option>
                      <option value="Compliance Policy">Compliance Policy</option>
                      <option value="Security Policy">Security Policy</option>
                      <option value="Environmental Policy">Environmental Policy</option>
                      <option value="Financial Policy">Financial Policy</option>
                      <option value="Operational Policy">Operational Policy</option>
                      <option value="IT Policy">IT Policy</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Issuing Authority</label>
                    <input type="text" value={metadataForm.issuing_authority}
                      onChange={(e) => setMetadataForm({...metadataForm, issuing_authority: e.target.value})}
                      placeholder="e.g., HR Department, Board of Directors"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Effective Date</label>
                    <input type="text" value={metadataForm.effective_date}
                      onChange={(e) => setMetadataForm({...metadataForm, effective_date: e.target.value})}
                      placeholder="e.g., January 1, 2025"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Review Cycle</label>
                    <select value={metadataForm.review_cycle}
                      onChange={(e) => setMetadataForm({...metadataForm, review_cycle: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500">
                      <option value="">Select cycle...</option>
                      <option value="Annual">Annual</option>
                      <option value="Biannual">Biannual</option>
                      <option value="Quarterly">Quarterly</option>
                      <option value="As Needed">As Needed</option>
                      <option value="Not Specified">Not Specified</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Applicability Scope</label>
                    <input type="text" value={metadataForm.applicability_scope}
                      onChange={(e) => setMetadataForm({...metadataForm, applicability_scope: e.target.value})}
                      placeholder="e.g., All Employees, IT Department, Contractors"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
                  </div>
                </div>
                <div className="flex gap-3 mt-4">
                  <button type="submit" className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                    {analysis && analysis.status === 'processing' ? 'Update Metadata' : 'Save Metadata'}
                  </button>
                  {isEditingMetadata && (
                    <button type="button" onClick={handleCancelEdit}
                      className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Cancel</button>
                  )}
                </div>
              </form>
            )}
          </div>

          {/* Summary */}
          {analysis.summary && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Summary</h3>
              <div className="prose prose-sm max-w-none text-gray-700">
                {analysis.summary.split('\n').map((paragraph, idx) => (
                  <p key={idx} className="mb-3 whitespace-pre-wrap">{paragraph}</p>
                ))}
              </div>
            </div>
          )}

          {/* Responsibility & Process Diagrams */}
          {analysis.diagrams && (
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <svg className="w-6 h-6 text-indigo-600 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
                </svg>
                Responsibilities & Processes
              </h3>
              {analysis.diagrams.obligationsDiagram && (
                <PolicyDiagramRenderer diagramData={analysis.diagrams.obligationsDiagram} />
              )}
              {analysis.diagrams.processDiagrams && analysis.diagrams.processDiagrams.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Processes</h4>
                  {analysis.diagrams.processDiagrams.map((processDiagram, idx) => (
                    <ProcessDiagramAccordion key={idx} diagramData={processDiagram} />
                  ))}
                </div>
              )}
              {!analysis.diagrams.obligationsDiagram && (!analysis.diagrams.processDiagrams || analysis.diagrams.processDiagrams.length === 0) && (
                <p className="text-gray-500 italic">No diagrams were generated for this policy.</p>
              )}
            </div>
          )}

          {/* Dimension Results */}
          {results.map((result, idx) => (
            <div key={idx} className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <span className="w-8 h-8 rounded-full bg-primary-100 text-primary-700 flex items-center justify-center text-sm font-bold mr-3">{idx + 1}</span>
                {result.dimension_name}
              </h3>
              <div className="mb-6">
                <h4 className="text-sm font-semibold text-gray-700 mb-2 uppercase tracking-wide">Analysis</h4>
                <div className="prose prose-sm max-w-none text-gray-700 bg-gray-50 rounded-lg p-4">{result.textual_analysis}</div>
              </div>
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3 uppercase tracking-wide">Visualization</h4>
                <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">{renderVisualization(result)}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Recent Analyses List */}
      {!loading && !id && recentAnalyses.length > 0 && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-bold text-gray-900">Analyzed Policies ({filteredAnalyses.length})</h3>
          </div>

          {/* Folder Navigation */}
          {user && (
            <div className="flex flex-wrap items-center gap-2 mb-4 pb-4 border-b border-gray-200">
              <button onClick={() => setSelectedFolder(null)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedFolder === null ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}>All ({recentAnalyses.length})</button>
              <button onClick={() => setSelectedFolder('unfiled')}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  selectedFolder === 'unfiled' ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}>Unfiled ({recentAnalyses.filter(a => !a.folder_id).length})</button>
              {folders.map(f => (
                <div key={f.uuid} className="relative group inline-flex items-center">
                  <button onClick={() => setSelectedFolder(f.uuid)}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                      selectedFolder === f.uuid ? 'bg-primary-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}>{f.name} ({f.policy_count})</button>
                  <button onClick={(e) => { e.stopPropagation(); setFolderModalMode('rename'); setFolderModalTarget(f.uuid); setFolderModalName(f.name); setFolderModalOpen(true); }}
                    className="ml-1 text-gray-400 hover:text-gray-600 text-xs opacity-0 group-hover:opacity-100 transition-opacity" title="Rename folder">&#9998;</button>
                  <button onClick={(e) => { e.stopPropagation(); handleDeletePolicyFolder(f.uuid); }}
                    className="ml-0.5 text-gray-400 hover:text-red-600 text-xs opacity-0 group-hover:opacity-100 transition-opacity" title="Delete folder">&#10005;</button>
                </div>
              ))}
              <button onClick={() => { setFolderModalMode('create'); setFolderModalName(''); setFolderModalOpen(true); }}
                className="px-3 py-1.5 rounded-full text-sm font-medium bg-gray-100 text-primary-600 hover:bg-primary-50 border border-dashed border-primary-300">+ New Folder</button>
            </div>
          )}

          {/* Search */}
          <div className="mb-4">
            <input type="text" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by title, URL, type, or authority..."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500" />
          </div>

          {/* List */}
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {filteredAnalyses.length === 0 ? (
              <p className="text-gray-500 text-center py-4">No analyses match your search</p>
            ) : (
              filteredAnalyses.map(item => (
                <div key={item.uuid}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg transition-colors ${
                    analysis?.uuid === item.uuid ? 'bg-primary-50 border-2 border-primary-500' : 'bg-gray-50'
                  }`}>
                  <button onClick={() => navigate(`/policy-analysis/${item.uuid}`)}
                    className="flex-1 text-left hover:opacity-80 transition-opacity">
                    <p className="font-medium text-gray-900">{item.title}</p>
                    <p className="text-sm text-gray-500 mt-1">
                      {new Date(item.created_at).toLocaleDateString()} •
                      <span className={`ml-1 ${item.status === 'completed' ? 'text-green-600' : item.status === 'processing' ? 'text-yellow-600' : 'text-red-600'}`}>
                        {item.status}
                      </span>
                    </p>
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {item.folder_id && (() => {
                        const folder = folders.find(f => f.id === item.folder_id);
                        return folder ? <span className="px-2 py-0.5 rounded text-xs font-medium bg-primary-100 text-primary-700">{folder.name}</span> : null;
                      })()}
                      {item.policy_type && (
                        <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase ${
                          item.policy_type.includes('Government') ? 'bg-blue-100 text-blue-800' :
                          item.policy_type.includes('HR') ? 'bg-purple-100 text-purple-800' :
                          item.policy_type.includes('Compliance') ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>{item.policy_type}</span>
                      )}
                      {item.issuing_authority && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-700">{item.issuing_authority}</span>
                      )}
                      {item.applicability_scope && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700">{item.applicability_scope}</span>
                      )}
                    </div>
                  </button>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {user && folders.length > 0 && (
                      <select value={item.folder_id ? (folders.find(f => f.id === item.folder_id)?.uuid || '') : ''}
                        onChange={(e) => handleMovePolicyToFolder(item.uuid, e.target.value || null)}
                        className="text-xs border border-gray-200 rounded px-1.5 py-1 bg-white text-gray-600 cursor-pointer" title="Move to folder">
                        <option value="">Unfiled</option>
                        {folders.map(f => <option key={f.uuid} value={f.uuid}>{f.name}</option>)}
                      </select>
                    )}
                    {isAdmin && (
                      <button onClick={(e) => handleDeleteFromList(item.uuid, e)}
                        className="px-3 py-1.5 text-xs bg-red-600 text-white rounded hover:bg-red-700" title="Delete this analysis">Delete</button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Folder Modal */}
      {folderModalOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="bg-white rounded-lg shadow-xl p-6" style={{ width: '400px' }}>
            <h3 className="text-lg font-bold mb-4">{folderModalMode === 'create' ? 'New Folder' : 'Rename Folder'}</h3>
            <input type="text" value={folderModalName} onChange={(e) => setFolderModalName(e.target.value)}
              placeholder="Folder name" className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 mb-4"
              autoFocus onKeyDown={(e) => { if (e.key === 'Enter') { folderModalMode === 'create' ? handleCreatePolicyFolder() : handleRenamePolicyFolder(); } }} />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setFolderModalOpen(false); setFolderModalName(''); }}
                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Cancel</button>
              <button onClick={folderModalMode === 'create' ? handleCreatePolicyFolder : handleRenamePolicyFolder}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                {folderModalMode === 'create' ? 'Create' : 'Rename'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}

export default PolicyAnalysis;
